// biome-ignore-all lint/suspicious/noArrayIndexKey: progress dots are
//   indexed by step number; TOTAL_STEPS is constant and never reorders.
//
// Plan 08-06 — Tutorial screen wrapper.
// Phase 17 Plan 06 Task 3 (cleanup-reskin) — token + Btn re-skin with
// rg-screen.tutorial class + display-word title + progress dots + yellow
// NEXT Btn. The host's button-driven step flow is preserved as the inner
// runner; this wrapper now also provides the top-of-screen Skip Btn,
// display-word title, and progress-dot indicator (UI-SPEC §7 row 8).

import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import { markTutorialComplete } from '../../stores/tutorial.js'
import { Btn } from '../components/Btn'
import { SignupSoftPrompt } from '../signup/SignupSoftPrompt.js'
import { TutorialMatchHost } from './TutorialMatchHost.js'

export function TutorialScreen(): JSX.Element {
  useDocumentTitle('doc.title.tutorial')
  const router = useRouter()
  const [showSignup, setShowSignup] = useState(false)
  // FLOW-07 — progress dots are fed by the host's real interactive step (no
  // more dead local counter / duplicate advance button).
  const [progress, setProgress] = useState<{ index: number; total: number }>({
    index: 0,
    total: 1,
  })

  const handleTutorialComplete = useCallback(() => {
    markTutorialComplete()
    setShowSignup(true)
  }, [])

  const handleSignupClose = useCallback(() => {
    setShowSignup(false)
    router.navigate('/lobby')
  }, [router])

  const handleSkip = useCallback(() => {
    markTutorialComplete()
    router.navigate('/lobby')
  }, [router])

  const handleStepChange = useCallback((index: number, total: number) => {
    setProgress({ index, total })
  }, [])

  return (
    <main
      id="main"
      className="rg-screen tutorial"
      tabIndex={-1}
      data-screen="tutorial"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 'var(--space-4)',
          right: 'var(--space-4)',
          zIndex: 10,
        }}
      >
        <Btn kind="ghost" size="sm" onClick={handleSkip} testId="tutorial-skip">
          <FormattedMessage id="tutorial.skip" defaultMessage="SKIP" />
        </Btn>
      </div>
      {/* Solid folk-ink title (was .display-word.gradient = gold-gradient-
          clipped text that washed out / was unreadable on the cream paper). */}
      <h1
        style={{
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-36)',
          textAlign: 'center',
          margin: 'var(--space-5) 0 var(--space-3)',
          color: 'var(--color-text-primary)',
        }}
      >
        <FormattedMessage
          id="onboarding.tutorial.title"
          defaultMessage="The runes await"
        />
      </h1>
      <TutorialMatchHost
        onComplete={handleTutorialComplete}
        onStepChange={handleStepChange}
      />
      {/* FLOW-07 — read-only progress indicator fed by the host's true step.
          The host owns the single advance control (no duplicate NEXT/DONE). */}
      <div
        className="progress-dots"
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          justifyContent: 'center',
          margin: 'var(--space-3) 0 var(--space-5)',
        }}
        aria-hidden="true"
      >
        {Array.from({ length: progress.total }).map((_, i) => {
          // Folk parity (prototype TutorialScreen): the dots up to + including
          // the current step are Ember (--color-player-a); the active one
          // widens into a pill. Width/colour transitions are globally
          // reduced-motion-capped by the globals.css OS query + the in-app
          // [data-motion="reduced"] floor.
          const reached = i <= progress.index
          const active = i === progress.index
          return (
            <span
              key={`tutorial-dot-${i}`}
              className={`dot ${active ? 'active' : ''}`}
              style={{
                width: active ? '22px' : 'var(--space-2)',
                height: 'var(--space-2)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                background: reached
                  ? 'var(--color-player-a)'
                  : 'var(--color-ink-ghost)',
                transition:
                  'width var(--duration-med) var(--ease-bounce), background var(--duration-med) var(--ease-out)',
              }}
            />
          )
        })}
      </div>
      <SignupSoftPrompt open={showSignup} onClose={handleSignupClose} />
    </main>
  )
}
